import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabase';
import { useDarkMode } from '../../context/DarkModeContext';
import { useToast } from '../../context/ToastContext';
import { useGoals } from '../../hooks/useGoals';
import { useRubric } from '../../hooks/useRubric';
import { motion, AnimatePresence } from 'framer-motion';
import { IoCloseOutline, IoCheckmarkOutline } from 'react-icons/io5';
import MappingPreview from './MappingPreview';
import {
  GoalList,
  RubricSelector,
  MappingInsights,
  MappingGoal,
  RubricCategory,
  SimpleMapping,
  calculateMappingInsights,
  transformMappingsToSimple,
  getMappingStyles
} from './shared';

export interface GoalRubricMapping {
  id: string;
  goal_id: string;
  focus_id: string;
  rubric_category: string;
  rubric_items: string[];
  created_at: Date;
}

interface MappingModalProps {
  focusId: string;
  isOpen: boolean;
  onClose: () => void;
}

const MappingModal: React.FC<MappingModalProps> = ({ focusId, isOpen, onClose }) => {
  const { darkMode } = useDarkMode();
  const { showToast } = useToast();
  const { goals, loading: goalsLoading } = useGoals(focusId);
  const { activeRubric, rubricLoading } = useRubric(focusId);
  const [mappings, setMappings] = useState<GoalRubricMapping[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'edit' | 'preview'>('edit');

  const styles = getMappingStyles(darkMode);
  const bgColor = darkMode ? 'bg-gray-900' : 'bg-white';
  const borderColor = styles.borderColor;
  const mutedTextColor = styles.mutedTextColor;
  const textColor = styles.textColor;

  // Fetch existing mappings
  useEffect(() => {
    if (!isOpen || !focusId) return;

    const fetchMappings = async () => {
      try {
        const { data, error } = await supabase
          .from('goal_rubric_mappings')
          .select('*')
          .eq('focus_id', focusId);

        if (error) throw error;
        setMappings(data || []);
      } catch (error) {
        console.error('Error fetching mappings:', error);
        showToast('Failed to load mappings', 'error');
      }
    };

    fetchMappings();
  }, [isOpen, focusId, showToast]);

  // Set initial selected goal
  useEffect(() => {
    if (goals && goals.length > 0 && !selectedGoalId) {
      setSelectedGoalId(goals[0].id);
    }
  }, [goals, selectedGoalId]);


  // Transform data to shared component formats
  const mappingGoals: MappingGoal[] = goals?.map(goal => ({
    id: goal.id,
    text: goal.goal_text,
    type: goal.goal_type as MappingGoal['type']
  })) || [];

  // Get rubric categories - normalize the data structure
  const rubricCategories: RubricCategory[] = activeRubric?.categories 
    ? activeRubric.categories.map(cat => ({
        id: cat.id, // Always use the actual UUID
        name: cat.name,
        // Handle different data structures based on migration status
        items: (() => {
          // If we have normalized criteria_items, use them
          if (cat.criteria_items && cat.criteria_items.length > 0) {
            return cat.criteria_items.map((criterion) => ({
              // Use the actual criterion ID from the database
              id: criterion.id,
              text: criterion.text
            }));
          }
          // Fall back to legacy string array
          if (Array.isArray(cat.criteria)) {
            return cat.criteria.map((criterion, index) => ({
              id: `${index}`,
              text: typeof criterion === 'string' ? criterion : criterion.text || ''
            }));
          }
          return [];
        })()
      }))
    : [
        {
          id: 'technical',
          name: 'Technical Skills',
          items: [
            { id: '0', text: 'Complete starter project independently' },
            { id: '1', text: 'Ship 3+ production features' },
            { id: '2', text: 'Debug complex issues without hand-holding' },
            { id: '3', text: 'Write comprehensive documentation' },
            { id: '4', text: 'Lead a technical design discussion' }
          ]
        },
        {
          id: 'communication',
          name: 'Communication',
          items: [
            { id: '0', text: 'Present work clearly in team meetings' },
            { id: '1', text: 'Proactively communicate blockers' },
            { id: '2', text: 'Collaborate effectively with cross-functional partners' }
          ]
        },
        {
          id: 'delivery',
          name: 'Project Delivery',
          items: [
            { id: '0', text: 'Meet all sprint commitments' },
            { id: '1', text: 'Deliver intern project on time' },
            { id: '2', text: 'Show measurable impact' }
          ]
        }
      ];

  // No transformation needed - mappings already use category UUIDs
  const displayMappings = mappings;

  // Transform mappings to simple format for shared components
  const simpleMappings: SimpleMapping[] = transformMappingsToSimple(displayMappings, 'modal');
  

  const handleMappingToggle = (categoryId: string, itemId: string) => {
    if (!selectedGoalId) return;

    setHasChanges(true);
    
    setMappings(prev => {
      const existingMapping = prev.find(m => 
        m.goal_id === selectedGoalId && m.rubric_category === categoryId
      );

      if (existingMapping) {
        // Update existing mapping
        const itemIndex = existingMapping.rubric_items.indexOf(itemId);
        if (itemIndex > -1) {
          // Remove item
          const newItems = existingMapping.rubric_items.filter(id => id !== itemId);
          if (newItems.length === 0) {
            // Remove mapping entirely if no items left
            return prev.filter(m => m.id !== existingMapping.id);
          }
          return prev.map(m => 
            m.id === existingMapping.id 
              ? { ...m, rubric_items: newItems }
              : m
          );
        } else {
          // Add item
          return prev.map(m => 
            m.id === existingMapping.id 
              ? { ...m, rubric_items: [...m.rubric_items, itemId] }
              : m
          );
        }
      } else {
        // Create new mapping
        return [...prev, {
          id: `temp-${Date.now()}`,
          goal_id: selectedGoalId,
          focus_id: focusId,
          rubric_category: categoryId,
          rubric_items: [itemId],
          created_at: new Date()
        }];
      }
    });
  };

  // Note: RubricSelector component uses its own isItemMapped function
  // that works with the SimpleMapping format using actual criterion IDs

  const getGoalMappingCount = (goalId: string) => {
    return mappings
      .filter(m => m.goal_id === goalId)
      .reduce((count, m) => count + m.rubric_items.length, 0);
  };

  // Create mapping counts for GoalList component
  const mappingCounts = mappingGoals.reduce((acc, goal) => {
    acc[goal.id] = getGoalMappingCount(goal.id);
    return acc;
  }, {} as Record<string, number>);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Delete all existing mappings for this focus
      const { error: deleteError } = await supabase
        .from('goal_rubric_mappings')
        .delete()
        .eq('focus_id', focusId);

      if (deleteError) throw deleteError;

      // Insert new mappings
      if (mappings.length > 0) {
        const mappingsToInsert = mappings.map(m => ({
          goal_id: m.goal_id,
          focus_id: m.focus_id,
          rubric_category: m.rubric_category, // Already using UUIDs
          rubric_items: m.rubric_items
        }));

        const { error: insertError } = await supabase
          .from('goal_rubric_mappings')
          .insert(mappingsToInsert);

        if (insertError) throw insertError;
      }

      showToast('Mappings saved successfully', 'success');
      setHasChanges(false);
      onClose();
    } catch (error) {
      console.error('Error saving mappings:', error);
      showToast('Failed to save mappings', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      const confirmClose = window.confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmClose) return;
    }
    onClose();
  };

  // Calculate insights using shared utility
  const insights = calculateMappingInsights(rubricCategories, simpleMappings);
  const selectedGoal = mappingGoals.find(g => g.id === selectedGoalId) || null;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/30" />

        {/* Modal */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className={`relative w-full max-w-4xl max-h-[85vh] ${bgColor} rounded-xl shadow-2xl border ${borderColor} flex flex-col`}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`flex items-center justify-between p-6 border-b ${borderColor}`}>
            <h2 className={`text-2xl font-semibold ${textColor}`}>
              Connect goals to evaluation criteria
            </h2>
            <button
              onClick={handleClose}
              className={`p-2 rounded-lg hover:bg-gray-700 transition-colors ${mutedTextColor}`}
            >
              <IoCloseOutline className="w-6 h-6" />
            </button>
          </div>

          {/* Description */}
          <div className={`px-6 pt-4 ${mutedTextColor}`}>
            <p className="text-sm">
              Your personal goals might not perfectly align with your company's evaluation criteria—and that's okay. 
              This mapping helps track relevant evidence when there is overlap.
            </p>
          </div>

          {/* View Toggle - Outside scroll area */}
          <div className="px-6 pt-4 pb-6">
            {viewMode === 'edit' ? (
              <button
                onClick={() => setViewMode('preview')}
                className={`text-sm font-medium ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} transition-colors`}
              >
                Preview Connections →
              </button>
            ) : (
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setViewMode('edit')}
                  className={`text-sm font-medium ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} transition-colors`}
                >
                  ← Back to Edit
                </button>
                <p className={`text-sm ${mutedTextColor}`}>
                  Click on goals or criteria to highlight connections
                </p>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {goalsLoading || rubricLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
              </div>
            ) : viewMode === 'edit' ? (
              <div className="flex flex-col h-full">
                {/* Edit View */}
                <div className="p-6 flex gap-6 flex-1 min-h-0">
                  {/* Goals Selector */}
                  <GoalList
                    goals={mappingGoals}
                    selectedGoalId={selectedGoalId}
                    mappingCounts={mappingCounts}
                    onGoalSelect={setSelectedGoalId}
                    className="w-1/3 overflow-y-auto"
                  />

                  {/* Rubric Selector */}
                  <RubricSelector
                    categories={rubricCategories}
                    mappings={simpleMappings}
                    selectedGoalId={selectedGoalId}
                    onToggleMapping={handleMappingToggle}
                    onSelectAll={(categoryId, selectAll) => {
                      if (!selectedGoalId) return;
                      
                      if (selectAll) {
                        // Select all items in this category
                        setMappings(prev => {
                          const otherMappings = prev.filter(m => 
                            !(m.goal_id === selectedGoalId && m.rubric_category === categoryId)
                          );
                          const categoryItems = rubricCategories.find(c => c.id === categoryId)?.items || [];
                          return [...otherMappings, {
                            id: `temp-${Date.now()}-${categoryId}`,
                            goal_id: selectedGoalId,
                            focus_id: focusId,
                            rubric_category: categoryId,
                            rubric_items: categoryItems.map(item => item.id),
                            created_at: new Date()
                          }];
                        });
                      } else {
                        // Deselect all items in this category
                        setMappings(prev => prev.filter(m => 
                          !(m.goal_id === selectedGoalId && m.rubric_category === categoryId)
                        ));
                      }
                      setHasChanges(true);
                    }}
                    onSelectAllGlobal={(selectAll) => {
                      if (!selectedGoalId) return;
                      
                      if (selectAll) {
                        // Select all items for this goal
                        const newMappings: GoalRubricMapping[] = [];
                        rubricCategories.forEach(cat => {
                          if (cat.items.length > 0) {
                            newMappings.push({
                              id: `temp-${Date.now()}-${cat.id}`,
                              goal_id: selectedGoalId,
                              focus_id: focusId,
                              rubric_category: cat.id,
                              rubric_items: cat.items.map(item => item.id),
                              created_at: new Date()
                            });
                          }
                        });
                        setMappings(prev => [
                          ...prev.filter(m => m.goal_id !== selectedGoalId),
                          ...newMappings
                        ]);
                      } else {
                        // Deselect all - remove all mappings for this goal
                        setMappings(prev => prev.filter(m => m.goal_id !== selectedGoalId));
                      }
                      setHasChanges(true);
                    }}
                    className="flex-1 overflow-y-auto"
                  />
                </div>

                {/* Insights Section */}
                <MappingInsights
                  insights={insights}
                  selectedGoal={selectedGoal}
                  className="px-6 pb-4"
                />

                {/* Reminder Box */}
                <div className={`mx-6 mb-6 p-4 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-blue-50 border-blue-200'} border rounded-lg`}>
                  <p className={`text-sm ${styles.textColor}`}>
                    <strong>Remember:</strong> It's normal for personal goals and company evaluation criteria to not perfectly align. 
                    Focus on what matters most to you, while being aware of how you'll be evaluated.
                  </p>
                </div>
              </div>
            ) : (
              /* Preview View */
              <MappingPreview
                goals={goals || []}
                rubricCategories={rubricCategories}
                mappings={mappings}
                onBack={() => setViewMode('edit')}
                hideHeader={true}
              />
            )}
          </div>

          {/* Footer */}
          <div className={`flex items-center justify-between p-6 border-t ${borderColor}`}>
            <p className={`text-sm ${mutedTextColor}`}>
              {viewMode === 'edit' ? 'Select a goal and check the criteria it connects to' : ''}
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  darkMode 
                    ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                Cancel
              </button>
              {viewMode === 'edit' && (
                <button
                  onClick={handleSave}
                  disabled={isSaving || !hasChanges}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                    hasChanges && !isSaving
                      ? 'bg-blue-500 hover:bg-blue-600 text-white' 
                      : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <IoCheckmarkOutline className="w-5 h-5" />
                      Save Changes
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MappingModal;